import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import type { Membership, Tenant } from '../api/types.js';

export interface ActiveTenantInfo {
  tenant: Tenant | null;
  membership: Membership | null;
  /** Tenants visible to the user (intersection of memberships ∩ /api/tenants). */
  available: Tenant[];
}

/**
 * Resolve the active tenant from URL (`/t/:tenantId/...`).
 * Returns null tenant when the URL tenant is missing from the user's
 * memberships — caller must redirect to a valid tenant in that case.
 */
export function useActiveTenant(
  tenants: Tenant[] | undefined,
  memberships: Membership[] | undefined,
): ActiveTenantInfo {
  const { tenantId } = useParams<{ tenantId: string }>();

  return useMemo(() => {
    const ms = memberships ?? [];
    const ts = tenants ?? [];
    const allowedIds = new Set(ms.map((m) => m.tenantId));
    const available = ts.filter((t) => allowedIds.has(t.tenantId));
    if (!tenantId) return { tenant: null, membership: null, available };
    const tenant = available.find((t) => t.tenantId === tenantId) ?? null;
    const membership = ms.find((m) => m.tenantId === tenantId) ?? null;
    return { tenant, membership, available };
  }, [tenantId, tenants, memberships]);
}
